import React, { createContext, ReactElement, useContext } from 'react';
import { SlideOver } from './slide-over';
import { ContextualViewFunc, SlideOverFunc } from './types';
import { useSliderStates } from './use-slider-state';

interface Props {
  children: React.ReactElement<any>;
  contextualView?: ContextualViewFunc;
  /** Closes every panel of the shell that supplied `contextualView`. */
  closeAllSlideOvers?: () => void;
}

const noImplementation = () => {
  throw new Error('Contextual view not passed from Container Interface');
};

export const SlideOverContext = createContext<{
  contextualView: ContextualViewFunc;
  closeAllSlideOvers?: () => void;
  deleteSlideOver: (id: string) => void;
  slideOver: SlideOverFunc;
}>({
  slideOver: () => {
    return () => {
      throw new Error('Not implemented');
    };
  },
  contextualView: () => {
    return () => {
      throw new Error('Not implemented');
    };
  },
  deleteSlideOver: () => {
    throw new Error('deleteSlideOver is not implemented');
  },
});

export const SlideOverProvider = ({
  children,
  contextualView,
  closeAllSlideOvers,
}: Props): ReactElement<any> => {
  const { deleteAllSlideOvers, deleteSlideOver, slideOver, slideOvers } =
    useSliderStates();
  return (
    <SlideOverContext.Provider
      value={{
        contextualView: contextualView || noImplementation,
        // This provider's own panels (opened with `slideOver`) and the shell's.
        closeAllSlideOvers: () => {
          deleteAllSlideOvers();
          closeAllSlideOvers?.();
        },
        deleteSlideOver,
        slideOver,
      }}
    >
      <>
        {children}
        {slideOvers?.map(
          ({ isOpen, id, renderKey, children, ...sliderProps }) => (
            <SlideOver
              key={renderKey}
              isOpen={isOpen}
              overlay
              setIsOpen={() => deleteSlideOver(id)}
              {...sliderProps}
            >
              {children}
            </SlideOver>
          ),
        )}
      </>
    </SlideOverContext.Provider>
  );
};

export const useSlideOver = () => {
  const context = useContext(SlideOverContext);
  if (context === undefined) {
    throw new Error('SlideOver context is undefined');
  }
  return context;
};
